import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  DrawnTogetherPhase,
  MoveDurationByFormat,
  MoveType,
  PromptFormat,
} from '@shared/constants';
import {
  ActivateFactOrFibData,
  DrawingAssignment,
  PlayerResult,
  RoomResult,
} from '@shared/api-dto';
import { tallySideBets } from '@shared/side-bets';
import {
  allPlayersResponded,
  getNextPrompt,
  getTeamName,
  getTeamUp,
  isAllPlay,
  isGameOver,
  isPreMove,
  moveWasSubmitted,
  teamIsMoving,
  useFactOrFibRadios,
  useNextOpponent,
  useNextPlayer,
  UserAnswersByPrompt,
  useTallies,
  useTeams,
  whileMoving,
  quote,
} from './shared/room-data-helpers';
import { activateMove, endMove, playAgain, setMovePhase, submitMove } from '../../api/endpoints';
import { Timer } from './shared/timer';
import { RadioGroup } from './shared/radio-group';
import { Modal } from './shared/modal';
import { TeamList } from './shared/team-list';
import { RoundTypePicker, useSelectedRoundTypes } from './shared/round-type-picker';
import { GameAwards } from './shared/game-awards';
import { EndMoveMarquee, EndMarqueeAllPlay, EndMarqueeDrawnTogether, GameOverMarquee } from './marquees/end-move-marquee';
import { StartMoveMarquee } from './marquees/start-move-marquee';
import { DrawnTogetherOverlay, getSelectedPosterUrl } from './drawn-together/drawn-together-overlay';

type GameRoomProps = {
  roomData: RoomResult;
};

export const GameRoom = ({ roomData }: GameRoomProps) => {
  const [modalActive, setModalActive] = useState<boolean>(false);
  const { selected, toggle, hasAny } = useSelectedRoundTypes();
  const [team1, team2] = useTeams(roomData.players);

  const teamUp = getTeamUp(roomData);
  const nextPrompt = getNextPrompt(roomData);
  const nextPlayer = useNextPlayer(roomData);
  const nextOpponent = useNextOpponent(team1, team2, nextPlayer);

  const {
    currentResponseCounts,
    correctAnswer,
    scoresByPlayer,
    scoresByTeam,
    userAnswersByPrompt,
    allPlayAvgByPrompt
  } = useTallies(roomData);

  // Check for All Play complete.
  useEffect(() => {
    if (!roomData.currentMove || roomData.currentMove.type !== MoveType.SLIDER_ALL_PLAY) {
      return;
    }

    if (roomData.currentMove.submitted) {
      return;
    }

    if (allPlayersResponded(roomData.currentMove.prompt.guid, userAnswersByPrompt, roomData)) {
      (async () => {
        try {
          await submitMove({
            code: roomData.code,
            promptGuid: roomData.currentMove!.prompt.guid
          });
        } catch (e) {
          console.error('could not submit choice', e);
        }
      })();
    }
  }, [roomData.currentMove?.prompt.guid, userAnswersByPrompt, roomData.players]);

  // Drawn Together: once every player has picked a poster, start the showcase;
  // once every player has voted, close the vote.
  const drawnTogetherPhase = roomData.currentMove?.phase ?? DrawnTogetherPhase.DRAWING;
  const advancingPhaseRef = useRef<string | null>(null);
  useEffect(() => {
    const move = roomData.currentMove;
    if (!move || move.type !== MoveType.DRAWN_TOGETHER || move.submitted) {
      return;
    }

    const assignments = move.drawingAssignments ?? [];
    if (assignments.length === 0) {
      return;
    }

    const advanceOnce = async (phase: string, action: () => Promise<unknown>) => {
      // Scoped to the move so a later Drawn Together round starts fresh.
      const target = `${move.prompt.guid}:${phase}`;
      if (advancingPhaseRef.current === target) {
        return;
      }
      advancingPhaseRef.current = target;
      try {
        await action();
      } catch (e) {
        console.error('could not advance drawn together move', e);
        advancingPhaseRef.current = null;
      }
    };

    if (drawnTogetherPhase === DrawnTogetherPhase.DRAWING) {
      if (assignments.every(getSelectedPosterUrl)) {
        advanceOnce(DrawnTogetherPhase.PRESENTING, () => setMovePhase({
          code: roomData.code,
          phase: DrawnTogetherPhase.PRESENTING,
        }));
      }
      return;
    }

    if (drawnTogetherPhase === DrawnTogetherPhase.VOTING) {
      const voteCount = assignments.reduce((sum, da) => sum + da.voterGuids.length, 0);
      if (voteCount >= roomData.players.length) {
        advanceOnce('submitted', () => submitMove({
          code: roomData.code,
          promptGuid: move.prompt.guid,
        }));
      }
    }
  }, [roomData.currentMove, drawnTogetherPhase, roomData.players.length]);

  const onShowcaseComplete = async () => {
    try {
      await setMovePhase({
        code: roomData.code,
        phase: DrawnTogetherPhase.VOTING,
      });
    } catch (e) {
      console.error('could not open voting', e);
    }
  };

  const onModalCancel = () => {
    setModalActive(false);
  };

  const onModalConfirm = async () => {
    setModalActive(false);
    try {
      const { newCode } = await playAgain({
        code: roomData.code,
        promptFormats: selected,
      });
      window.location.href = `/room/${newCode}`;
    } catch (e) {
      console.error('could not play again', e);
    }
  };

  const isTeamUp = (teamNumber: number) => {
    if (!roomData.currentMove) {
      return false;
    }

    return teamNumber === teamUp;
  };

  const setUpMove = async () => {
    try {
      let factOrFibData: ActivateFactOrFibData | undefined;
      let drawingAssignments: DrawingAssignment[] | undefined;

      if (nextPrompt.type === PromptFormat.FREE_INPUT) {
        if (!nextOpponent) {
          console.error('no opponent found');
          return;
        }

        factOrFibData = {
          authorGuid: nextOpponent.user.guid
        };
      } else if (nextPrompt.type === PromptFormat.DRAWING) {
        // Get all players and their responses to nextPrompt
        const players = roomData.players;
        if (players.length < 2) {
          console.error('need at least 2 players for drawing move');
          return;
        }

        const responses = roomData.responses.filter(r => r.promptGuid === nextPrompt.guid);
        if (responses.length === 0) {
          console.error('no responses to draw from for this prompt');
          return;
        }

        if (responses.length !== players.length) {
          console.error('number of responses does not match number of players');
          return;
        }

        // Create a randomized circular assignment
        drawingAssignments = [];
        const shuffledPlayers = [...players].sort(() => Math.random() - 0.5);

        // Each player draws the next player's response
        for (let i = 0; i < shuffledPlayers.length; i++) {
          const drawer = shuffledPlayers[i];
          const nextPlayer = shuffledPlayers[(i + 1) % shuffledPlayers.length];

          drawingAssignments.push({
            drawerGuid: drawer.user.guid,
            responseToDrawGuid: nextPlayer.user.guid,
          });
        }
      }

      await activateMove({
        code: roomData.code,
        playerGuid: isAllPlay(roomData) ? undefined : nextPlayer?.user.guid,
        promptGuid: nextPrompt.guid,
        duration: MoveDurationByFormat[nextPrompt.type],
        factOrFibData,
        drawingAssignments
      });
    } catch (e) {
      console.error('failed to activate player', e);
    }
  };

  const tearDownMove = async () => {
    if (!roomData.currentMove?.prompt) {
      console.error('no move to end');
      return;
    }

    try {
      await endMove({
        code: roomData.code,
        promptGuid: roomData.currentMove?.prompt.guid
      });
    } catch (e) {
      console.error('failed to tear down move', e);
    }
  };

  const onTimeUp = async () => {
    if (!roomData.currentMove) {
      console.error('time ran out without current move');
      return;
    }

    try {
      await submitMove({
        code: roomData.code,
        promptGuid: roomData.currentMove.prompt.guid
      });
    } catch (e) {
      console.error('could not submit choice', e);
    }
  };

  const onGameOver = () => {
    setModalActive(true);
  };

  const getTeamPanel = (teamNum: number) => {
    let className = teamNum === 1 ? 'leftTeamContainer' : 'rightTeamContainer';
    // After submit, show both teams' score panels so the standings marquee
    // can un-blur and let people read the totals.
    const showMoveUi = teamIsMoving(teamNum, roomData) && !moveWasSubmitted(roomData);
    if (showMoveUi && isTeamUp(teamNum)) {
      className += ' full';
    }

    return (
      <div className={className}>
        {showMoveUi
          ? (
            <>
              {/* All-play moves mount their single timer in the overlay instead. */}
              {!isAllPlay(roomData) && (
                <Timer key={'timer' + roomData.currentMove!.prompt.guid}
                  initialSeconds={roomData.timeLeft! / 1000}
                  hide={!whileMoving(roomData) || moveWasSubmitted(roomData)}
                  onComplete={onTimeUp}
                />
              )}
              {roomData.currentMove?.type === MoveType.FREE_FACT_OR_FIB && (
                <FactOrFibPanel
                  roomData={roomData}
                  userAnswersByPrompt={userAnswersByPrompt} />
              )}
              {roomData.currentMove?.type === MoveType.MC_READ_THE_ROOM && (
                <MultiChoicePanel roomData={roomData} />
              )}
              {roomData.currentMove?.type === MoveType.SLIDER_ALL_PLAY && (
                <AllPlayPanel
                  roomData={roomData}
                  team={teamNum === 1 ? team1 : team2}
                  userAnwersByPrompt={userAnswersByPrompt}
                  name={`Team ${getTeamName(teamNum)}`} />
              )}
            </>
            )
          : (
            <>
              <h2 className='teamTitle'>{`Team ${getTeamName(teamNum)}`}</h2>
              <TeamList team={teamNum === 1 ? team1 : team2} scores={scoresByPlayer} teamNumber={teamNum} />
              <hr className='scoreDivider' />
              <p className='scoreContainer'>{`Score: ${scoresByTeam[teamNum] ?? 0}`}</p>
            </>
            )}
      </div>
    );
  };

  return (
    <>
      {isGameOver(roomData) && (
        <>
          <GameOverMarquee
            roomData={roomData}
            scoresByTeam={scoresByTeam}
            onComplete={onGameOver} />
        </>
      )}
      {isPreMove(roomData) && (
        <StartMoveMarquee
          roomData={roomData}
          nextPlayer={isAllPlay(roomData) ? undefined : nextPlayer}
          onComplete={setUpMove}
          nextOpponent={nextOpponent}
        />
      )}
      {moveWasSubmitted(roomData) && !isAllPlay(roomData) && (
        <EndMoveMarquee
          roomData={roomData}
          correctAnswer={correctAnswer}
          currentResponseCounts={currentResponseCounts}
          answersByUser={userAnswersByPrompt[roomData.currentMove!.prompt.guid]}
          scoresByTeam={scoresByTeam}
          onComplete={tearDownMove} />
      )}
      {moveWasSubmitted(roomData) && roomData.currentMove?.type === MoveType.SLIDER_ALL_PLAY && (
        <EndMarqueeAllPlay
          roomData={roomData}
          userAnswersByPrompt={userAnswersByPrompt}
          allPlayAvgByPrompt={allPlayAvgByPrompt}
          scoresByTeam={scoresByTeam}
          onComplete={tearDownMove} />
      )}
      {moveWasSubmitted(roomData) && roomData.currentMove?.type === MoveType.DRAWN_TOGETHER && (
        <EndMarqueeDrawnTogether
          roomData={roomData}
          scoresByTeam={scoresByTeam}
          onComplete={tearDownMove} />
      )}
      <div className='teamPanels'>
        {getTeamPanel(1)}
        {getTeamPanel(2)}
      </div>
      {!isPreMove(roomData) && isAllPlay(roomData) && roomData.currentMove && !moveWasSubmitted(roomData) && (
        <AllPlayOverlay roomData={roomData} onShowcaseComplete={onShowcaseComplete}>
          {/* Drawn Together only runs the clock during voting; the key resets
              it when the phase changes. */}
          <Timer key={'timer' + roomData.currentMove.prompt.guid + (roomData.currentMove.phase ?? '')}
            initialSeconds={roomData.timeLeft! / 1000}
            hide={
              !whileMoving(roomData) || moveWasSubmitted(roomData) ||
              (roomData.currentMove.type === MoveType.DRAWN_TOGETHER && drawnTogetherPhase !== DrawnTogetherPhase.VOTING)
            }
            onComplete={onTimeUp}
          />
        </AllPlayOverlay>
      )}
      <Modal
        isActive={modalActive}
        className='playAgain'
        message='Play Again?'
        subtitle='Same players, fresh questions'
        confirmMessage={'Let\'s go!'}
        cancelMessage='Not now'
        confirmDisabled={!hasAny}
        onCancel={onModalCancel}
        onConfirm={onModalConfirm}>
        <GameAwards roomData={roomData} />
        <RoundTypePicker selected={selected} onToggle={toggle} stacked />
      </Modal>
    </>
  );
};

type AllPlayPanelProps = {
  roomData: RoomResult;
  team: PlayerResult[];
  userAnwersByPrompt: UserAnswersByPrompt;
  name: string;
};

const AllPlayPanel = ({ roomData, team, userAnwersByPrompt, name }: AllPlayPanelProps) => {
  const userAnswers = userAnwersByPrompt[roomData.currentMove!.prompt.guid];
  const radios = useMemo(() => (
    team.map(p => ({
      text: p.user.name,
      checked: !!userAnswers?.[p.user.guid],
      name: p.user.name
    }))
  ), [team, userAnswers]);

  return (
    <div className='allPlay'>
      <h2 className='teamTitle'>{name}</h2>
      <RadioGroup radios={radios} name={name} badge='initial' />
    </div>
  );
};

type AllPlayOverlayProps = {
  roomData: RoomResult;
  onShowcaseComplete: () => void;
  // Extra content pinned to the overlay, i.e. the round timer.
  children?: React.ReactNode;
};

const AllPlayOverlay = ({ roomData, onShowcaseComplete, children }: AllPlayOverlayProps) => {
  return (
    <div className={'allPlayOverlay' + (roomData.currentMove?.type === MoveType.DRAWN_TOGETHER ? ' drawnTogether' : '')}>
      {children}
      {roomData.currentMove?.type === MoveType.SLIDER_ALL_PLAY && (
        <>
          <div className='moveTop'>
            <span className='chevrons statusPill'>{'Everyone plays'}</span>
          </div>
          <p className='moveKicker'>Closest team average wins</p>
          <h2 className='moveQuestion'>{roomData.currentMove?.prompt.text}</h2>
          <p className='moveFooter'>Give your best guess on your phone</p>
        </>
      )}
      {roomData.currentMove?.type === MoveType.DRAWN_TOGETHER && (
        <DrawnTogetherOverlay roomData={roomData} onShowcaseComplete={onShowcaseComplete} />
      )}
    </div>
  );
};

type MoveTurnHeaderProps = {
  player: PlayerResult;
};

const MoveTurnHeader = ({ player }: MoveTurnHeaderProps) => {
  const teamName = getTeamName(player.team ?? 1);
  const teamClass = player.team === 2 ? 'team2' : 'team1';

  return (
    <>
      <div className='moveTop'>
        <span className='chevrons statusPill'>{`Team ${teamName}`}</span>
      </div>
      <h2 className={`moveTurn ${teamClass}`}>
        {`${player.user.name}'s turn`}
      </h2>
    </>
  );
};

type FactOrFibPanelProps = {
  roomData: RoomResult;
  userAnswersByPrompt: UserAnswersByPrompt;
};

const FactOrFibPanel = ({
  roomData,
  userAnswersByPrompt
}: FactOrFibPanelProps) => {
  if (!roomData.currentMove?.factOrFib) {
    console.error('rendering fact or fib panel without data');
    return null;
  }

  const player = roomData.currentMove.player;
  const { author } = roomData.currentMove.factOrFib;

  const move = roomData.currentMove;
  const prompt = move.prompt;

  const radios = useFactOrFibRadios(roomData, userAnswersByPrompt[move.prompt.guid], roomData.currentMove?.text || null);

  if (!player) {
    console.error('fact or fib rendered without player');
    return null;
  }

  return (
    <div className='movePanel'>
      <MoveTurnHeader player={player} />
      <p className='moveKicker'>{`What did ${author?.user.name} say when asked:`}</p>
      <h2 className='moveQuestion'>{quote(prompt.text)}</h2>
      <RadioGroup radios={radios} columns={2} name='fof' />
      <SideBetTally roomData={roomData} />
      <p className='moveFooter'>
        {'Grill the other team \u00b7 They must answer, but they can lie \u00b7 Answer on their phone \u00b7 Everyone else can bet'}
      </p>
    </div>
  );
};

type MultiChoicePanelProps = {
  roomData: RoomResult;
};

const MultiChoicePanel = ({ roomData }: MultiChoicePanelProps) => {
  const player = roomData.currentMove!.player;
  const radios = useMemo(() => {
    return roomData.currentMove?.prompt.choices?.map(c => ({
      text: c,
      checked: c === roomData.currentMove?.text
    }));
  }, [roomData.currentMove?.prompt.choices]);

  if (!player) {
    console.error('multi-choice panel rendered without player');
    return null;
  }

  return (
    <div className='movePanel'>
      <MoveTurnHeader player={player} />
      <p className='moveKicker'>Which choice was the most popular?</p>
      <h2 className='moveQuestion'>{roomData.currentMove?.prompt.text}</h2>
      <RadioGroup radios={radios ?? []} columns={2} name='mc' />
      <SideBetTally roomData={roomData} />
      <p className='moveFooter'>
        {'Discuss together \u00b7 Answer on their phone \u00b7 Everyone else can bet'}
      </p>
    </div>
  );
};

type SideBetTallyProps = {
  roomData: RoomResult;
};

const SideBetTally = ({ roomData }: SideBetTallyProps) => {
  const move = roomData.currentMove;
  if (!move || move.submitted) {
    return null;
  }

  const benchCount = Math.max(0, roomData.players.length - (move.player ? 1 : 0));
  const { believe, doubt, undecided } = tallySideBets(move.sideBets ?? [], benchCount);

  return (
    <p className='sideBetTally'>
      {`Side bets: ${believe} believe \u00b7 ${doubt} doubt \u00b7 ${undecided} undecided`}
    </p>
  );
};
